import { Form } from "antd";
import { OkButtonProps } from "components/BaseModal";
import BaseModalForm from "components/BaseModalForm";
import CustomCollapse from "components/CustomCollapse";
import message from "components/CustomMessage";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import _, { isEmpty } from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";

import { getJiraCmdbCustomAttributesFields } from "../genericIntegrationTicketUtils";
import { CUSTOM_ATTRIBUTE_KEYS } from "./JiraCMDBConstants";

type JiraCMDBOptionsProps = {
  label: string;
  value: string;
};

export type NameIdProps = {
  id: string | number;
  name: string;
};

export type CMDBSchemaType = {
  id?: string | number | null;
  name?: string;
};

export type JiraCMDBDataProps = {
  id?: string;
  loginId?: number;
  discoveryType?: string;
  schema?: NameIdProps;
  attributes?: string[];
  schemaType?: NameIdProps;
  customFields?: { key: string; value: string }[];
};

type JiraCMDBFormProps = {
  data?: JiraCMDBDataProps;
  loginConfigId?: any;
  onUpdateConfigComponent?: () => any;
  okButtonProps?: OkButtonProps;
};

const JiraCMDBMappingForm = (
  { data, loginConfigId, onUpdateConfigComponent, ...props }: JiraCMDBFormProps,
  ref: React.ForwardedRef<any>,
) => {
  const [schemaOptions, setSchemaOptions] = useState<JiraCMDBOptionsProps[]>();
  const [formData, setFormData] = useState<JiraCMDBDataProps>(() => {
    if (!isEmpty(data)) {
      if (data.customFields && data.customFields.length > 0) {
        for (const customAttribute of data.customFields) {
          data = {
            ...data,
            [customAttribute.key]: customAttribute.value,
          };
        }
      }
    }
    return {
      ...data,
      discoveryType: data?.discoveryType
        ? _.capitalize(data?.discoveryType)
        : "Asset",
    };
  });
  const [CMDBSchemaTypeOptions, setCMDBSchemaTypeOptions] =
    useState<JiraCMDBOptionsProps[]>();
  const [attributesOptions, setAttributesOptions] = useState<
    JiraCMDBOptionsProps[]
  >([]);
  const [selectedSchema, setSelectedSchema] = useState<any>(
    !isEmpty(data) ? data.schema : {},
  );
  const [selectedCmdbSchemaType, setSelectedCmdbSchemaType] = useState<any>(
    !isEmpty(data) ? data.schemaType : {},
  );
  const [form]: any = Form.useForm();
  const isEdit = !_.isNil(data?.loginId);

  //get schemas options
  useEffect(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getServiceNowCmdbCiTables({
      ticketSystemType: "JIRA",
      loginId: loginConfigId,
    });
    promise.then((response) => {
      setSchemaOptions(response ? response[0].schemas : []);
    });

    return cancel;
  }, [loginConfigId]);

  useEffect(() => {
    if (!isEmpty(selectedSchema) && !isEmpty(selectedCmdbSchemaType)) {
      const { promise, cancel } =
        ToolsIntegrationsAPI.getServiceNowCmdbCiAttributes({
          ticketSystemType: "JIRA",
          loginId: loginConfigId,
          schema: selectedSchema,
          schemaType: selectedCmdbSchemaType,
        });
      promise.then((response) => {
        setAttributesOptions(
          _.map(response.attributes, (a) => ({ label: a, value: a })),
        );
      });

      return cancel;
    }
    return;
  }, [loginConfigId, selectedSchema, selectedCmdbSchemaType]);

  useEffect(() => {
    if (isEmpty(selectedSchema)) {
      return;
    }
    const { promise, cancel } = ToolsIntegrationsAPI.getJiraCMDBObjectTypes({
      ticketSystemType: "JIRA",
      loginId: loginConfigId,
      schema: selectedSchema,
    });
    promise.then((response) => {
      if (response.success) {
        setCMDBSchemaTypeOptions(response.data.schemaTypes);
      }
    });

    return cancel;
  }, [loginConfigId, selectedSchema]);

  const fields = useMemo(
    () => [
      {
        name: "schema",
        label: "CMDB Object Schema",
        placeholder: "Select CMDB Object Schema",
        colSpan: 12,
        type: FieldTypes.SELECT,
        options: schemaOptions || [],
        disabled: isEdit,
        required: true,
        props: {
          onChange: (
            value: string,
            _: any,
            selectedOption: JiraCMDBOptionsProps,
          ) => {
            // setFormData((prevData) => ({ ...prevData, schema: value }));
            setSelectedSchema({
              id: selectedOption.value,
              name: selectedOption.label,
            });
            form.setFieldsValue({
              attributes: [],
              schemaType: null,
            });
          },
        },
      },
      {
        name: "schemaType",
        label: "CMDB Object Schema Type",
        placeholder: "Select CMDB Object Schema Type",
        colSpan: 12,
        type: FieldTypes.SELECT,
        options: CMDBSchemaTypeOptions || [],
        required: true,
        props: {
          onChange: (
            value: string,
            _: any,
            selectedOption: JiraCMDBOptionsProps,
          ) => {
            // setFormData((prevData) => ({ ...prevData, schemaType: value }));
            setSelectedCmdbSchemaType({
              id: selectedOption.value,
              name: selectedOption.label,
            });
            form.setFieldsValue({
              attributes: [],
            });
          },
        },
      },
      {
        name: "attributes",
        label: "CMDB Object Attributes",
        colSpan: 12,
        type: FieldTypes.MULTI_SELECT,
        options: attributesOptions || [],
        required: true,
      },
      {
        name: "discoveryType",
        label: "Type",
        colSpan: 12,
        disabled: isEdit,
        type: FieldTypes.SELECT,
        required: true,
        options: [
          { label: "Asset", value: "Asset" },
          { label: "Application", value: "Application" },
        ],
        props: {
          onChange: (value: string) => {
            setFormData((prevData) => ({ ...prevData, discoveryType: value }));
          },
        },
      },
      ...(formData.discoveryType === "Asset"
        ? [
            {
              colSpan: 24,
              render: () => (
                <CustomAttributesForm
                  attributesOption={attributesOptions}
                  discoveryType={formData.discoveryType || ""}
                />
              ),
            },
          ]
        : []),
    ],
    [
      schemaOptions,
      form,
      isEdit,
      attributesOptions,
      CMDBSchemaTypeOptions,
      formData.discoveryType,
    ],
  );

  const onSave = useCallback(
    async (_formData) => {
      await form.validateFields();
      const customAttributes = [];
      for (const attributeKey in CUSTOM_ATTRIBUTE_KEYS) {
        if (_formData[attributeKey]) {
          customAttributes.push({
            key: attributeKey,
            value: _formData[attributeKey],
          });
        }
      }
      const payload = {
        id: data?.id,
        loginId: loginConfigId,
        toolName: "JIRA",
        discoveryType: _formData.discoveryType.toUpperCase(),
        schema: selectedSchema,
        schemaType: selectedCmdbSchemaType,
        attributes: _formData.attributes,
        ...(!isEmpty(customAttributes)
          ? { customFields: customAttributes }
          : {}),
      };

      await ToolsIntegrationsAPI[
        isEdit ? "updateJiraCmdbMapping" : "saveJiraCmdbMapping"
      ](payload).promise;
      message.success(
        isEdit ? "Config updated successfully" : "Config created successfully",
      );
      onUpdateConfigComponent?.();
    },
    [
      form,
      loginConfigId,
      selectedCmdbSchemaType,
      selectedSchema,
      isEdit,
      onUpdateConfigComponent,
      data?.id,
    ],
  );

  return (
    <BaseModalForm
      afterClose={onUpdateConfigComponent}
      modalProps={{
        width: MODAL_SIZE_LARGE,
        okButtonProps: {
          permission: isEdit
            ? PermissionSetMap.EDIT_TOOL_CONFIG
            : PermissionSetMap.WRITE_TOOLS,
          ...(props?.okButtonProps || {}),
        },
      }}
      title={
        isEdit
          ? "Edit CMDB Discovery Assets/Applications"
          : "Create CMDB Discovery Assets/Applications"
      }
      onOk={onSave}
      fields={fields}
      form={form}
      initialValues={{
        ...formData,
        schema: formData.schema?.id,
        schemaType: formData.schemaType?.id,
      }}
      ref={ref}
      {...props}
    />
  );
};

export default forwardRef(JiraCMDBMappingForm);

const CustomAttributesForm = ({
  attributesOption,
  discoveryType,
}: {
  attributesOption: JiraCMDBOptionsProps[];
  discoveryType: string;
}) => {
  const customAttributesFields = useMemo(() => {
    return [
      {
        name: "hostname",
        title: "Host Name",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "ipAddress",
        title: "IP Address",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "dnsName",
        title: "DNS Name",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "os",
        title: "OS",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "osVersion",
        title: "OS Version",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "owner",
        title: "Owner",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "cloudProvider",
        title: "Cloud Provider",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "cloudAccountId",
        title: "Cloud Account ID",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "imageRepo",
        title: "Image Repo",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "registry",
        title: "Registry",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "cluster",
        title: "Cluster",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
      {
        name: "namespace",
        title: "Namespace",
        type: FieldTypes.SELECT,
        options: attributesOption,
        props: {
          allowClear: true,
        },
      },
    ];
  }, [attributesOption]);
  if (!discoveryType || discoveryType !== "Asset") {
    return null;
  }
  return (
    <CustomCollapse
      props={{}}
      defaultActiveKey={[]}
      header={"Configure Jira CMDB Attributes"}
      Key={""}
      description={getJiraCmdbCustomAttributesFields({
        customAttributesFields,
      })}
      type={"info"}
      content={null}
      className={""}
    />
  );
};
